import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Activity, AlertTriangle, BarChart3, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { ApiErrorModal } from '@components/ApiErrorModal';
import adminService from '@services/adminService';
import type { AIUsageCall, AIUsageDashboard, AIUsageGroup, TestResult } from '@/types/admin';
import { AI_FEATURE_LABELS, AI_PURPOSE_LABELS } from '@/types/admin';
import { formatDateBR, formatDateTimeBR, formatIntBR, formatUSD, todayISO } from '@/utils/formatters';
import { BudgetBar } from './BudgetBar';

const monthStart = () => `${todayISO().slice(0, 7)}-01`;

const Kpi: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="card p-3">
    <div className="text-small text-slate-500">{label}</div>
    <div className="text-xl font-semibold text-slate-800 mt-0.5">{value}</div>
    {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

/** Tabela agregada com barra proporcional ao custo. */
const GroupTable: React.FC<{ title: string; rows: AIUsageGroup[]; label?: (g: AIUsageGroup) => string }> = ({ title, rows, label }) => {
  const max = Math.max(...rows.map((r) => r.cost_usd), 0.000001);
  return (
    <section className="card p-4 space-y-2">
      <h2 className="text-h4">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-small text-slate-500">Sem chamadas no período.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="text-left">Item</th>
                <th className="text-right">Chamadas</th>
                <th className="text-right">Erros</th>
                <th className="text-right">Tokens (entrada / saída)</th>
                <th className="text-right">Custo</th>
                <th className="w-40"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.key} className="table-row">
                  <td className="table-cell">{label ? label(g) : g.label || g.key}</td>
                  <td className="table-cell text-right">{formatIntBR(g.calls)}</td>
                  <td className={clsx('table-cell text-right', g.errors > 0 && 'text-red-600')}>{formatIntBR(g.errors)}</td>
                  <td className="table-cell text-right whitespace-nowrap">
                    {formatIntBR(g.input_tokens + g.cache_read_tokens + g.cache_write_tokens)} / {formatIntBR(g.output_tokens)}
                  </td>
                  <td className="table-cell text-right whitespace-nowrap">{formatUSD(g.cost_usd)}</td>
                  <td className="table-cell">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${(g.cost_usd / max) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const CallTable: React.FC<{ title: string; rows: AIUsageCall[]; icon?: React.ReactNode }> = ({ title, rows, icon }) => (
  <section className="card p-4 space-y-2">
    <h2 className="text-h4 flex items-center gap-2">
      {icon}
      {title}
    </h2>
    {rows.length === 0 ? (
      <p className="text-small text-slate-500">Nada no período.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="table-header">
            <tr>
              <th className="text-left">Quando</th>
              <th className="text-left">Funcionalidade</th>
              <th className="text-left">Modelo</th>
              <th className="text-right">Tokens</th>
              <th className="text-right">Custo</th>
              <th className="text-right">Tempo</th>
              <th className="text-left">Origem</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="table-row align-top">
                <td className="table-cell whitespace-nowrap">{formatDateTimeBR(c.created_at)}</td>
                <td className="table-cell">
                  {AI_FEATURE_LABELS[c.feature] ?? c.feature}
                  {c.error_message && <div className="text-[11px] text-red-600 break-all max-w-md">{c.error_message}</div>}
                </td>
                <td className="table-cell font-mono text-xs">{c.model ?? '—'}</td>
                <td className="table-cell text-right whitespace-nowrap">
                  {formatIntBR(c.input_tokens)} / {formatIntBR(c.output_tokens)}
                </td>
                <td className="table-cell text-right">{formatUSD(c.cost_usd)}</td>
                <td className="table-cell text-right whitespace-nowrap">{(c.duration_ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s</td>
                <td className="table-cell">
                  {c.media_id ? (
                    <Link className="text-emerald-700 hover:underline" to={`/media?media=${c.media_id}`}>
                      Mídia #{c.media_id}
                    </Link>
                  ) : c.social_post_id ? (
                    <span>Post #{c.social_post_id}</span>
                  ) : (
                    <span className="text-slate-500">{c.user_email ?? 'sistema'}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

/** Gráfico de barras por dia (custo), sem biblioteca. */
const DailyChart: React.FC<{ rows: AIUsageGroup[] }> = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.cost_usd), 0.000001);
  return (
    <section className="card p-4 space-y-2">
      <h2 className="text-h4">Custo por dia</h2>
      {rows.length === 0 ? (
        <p className="text-small text-slate-500">Sem chamadas no período.</p>
      ) : (
        <div className="flex items-end gap-1 h-40 overflow-x-auto pb-6 relative">
          {rows.map((d) => (
            <div key={d.key} className="flex flex-col items-center justify-end h-full min-w-[22px] flex-1 group relative">
              <div
                className="w-full bg-emerald-500 rounded-t hover:bg-emerald-600"
                style={{ height: `${Math.max(2, (d.cost_usd / max) * 100)}%` }}
                title={`${formatDateBR(d.key)} · ${formatUSD(d.cost_usd)} · ${d.calls} chamada(s)`}
              />
              <span className="absolute -bottom-5 text-[10px] text-slate-400">{d.key.slice(8, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/** Admin → IA → Consumo: o que a IA do Marketing gastou, por funcionalidade, modelo, usuário e dia. */
export const AIUsageView: React.FC = () => {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayISO());
  const [purpose, setPurpose] = useState('');
  const [data, setData] = useState<AIUsageDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sync, setSync] = useState<TestResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminService.usage({ from, to, purpose }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [from, to, purpose]);

  useEffect(() => {
    load();
  }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      setSync(await adminService.syncProviderUsage());
      load();
    } catch (err) {
      setError(err);
    } finally {
      setSyncing(false);
    }
  };

  const t = data?.totals;
  const providerTotal = (data?.provider_usage ?? []).reduce((s, p) => s + (p.cost_usd ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h3 flex items-center gap-2">
            <BarChart3 size={20} className="text-slate-500" /> IA — Consumo
          </h1>
          <p className="text-small text-slate-500 mt-1">Cada chamada à IA feita pelo Marketing, com tokens, custo e origem.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="text-label">De</span>
            <DateInputBR value={from} onChange={(v) => setFrom(v || monthStart())} />
          </label>
          <label className="block">
            <span className="text-label">Até</span>
            <DateInputBR value={to} onChange={(v) => setTo(v || todayISO())} />
          </label>
          <label className="block">
            <span className="text-label">Propósito</span>
            <select className="select-field" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(AI_PURPOSE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} loading={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : (
        data &&
        t && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <Kpi label="Custo no período" value={formatUSD(t.cost_usd, 2)} hint={`${formatDateBR(data.from)} a ${formatDateBR(data.to)}`} />
              <Kpi label="Chamadas" value={formatIntBR(t.calls)} hint={t.errors ? `${formatIntBR(t.errors)} com erro` : 'nenhum erro'} />
              <Kpi
                label="Tokens"
                value={formatIntBR(t.input_tokens + t.cache_read_tokens + t.cache_write_tokens + t.output_tokens)}
                hint={`${formatIntBR(t.cache_read_tokens)} lidos do cache`}
              />
              <Kpi label="Custo médio por foto" value={formatUSD(data.avg_cost_per_image)} hint="catalogação + vetor" />
              <Kpi label="Custo médio por vídeo" value={formatUSD(data.avg_cost_per_video)} hint="catalogação + vetor" />
              <Kpi label="Custo médio por legenda" value={formatUSD(data.avg_cost_per_caption)} />
            </div>

            <section className="card p-4 space-y-4">
              <h2 className="text-h4">Orçamento do mês</h2>
              {data.budgets.map((b) => (
                <BudgetBar key={b.scope} budget={b} />
              ))}
              <Link to="/admin/ai/budget" className="text-small text-emerald-700 hover:underline">
                Alterar tetos
              </Link>
            </section>

            <DailyChart rows={data.by_day} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <GroupTable title="Por funcionalidade (agente)" rows={data.by_feature} />
              <GroupTable title="Por modelo" rows={data.by_model} />
            </div>
            <GroupTable title="Por usuário" rows={data.by_user} label={(g) => g.label || g.key} />

            <section className="card p-4 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-h4">Consumo informado pelos provedores</h2>
                <Button size="sm" variant="outline" icon={<RefreshCw />} loading={syncing} onClick={syncNow}>
                  Sincronizar agora
                </Button>
              </div>
              <p className="text-small text-slate-500">
                Lido da Admin API da Anthropic e da OpenAI (chaves em Admin → Integrações), filtrado pelo workspace/projeto do
                Marketing. Serve para conferir o consumo registrado pelo sistema.{' '}
                {data.provider_synced_at ? `Última leitura: ${formatDateTimeBR(data.provider_synced_at)}.` : 'Ainda não sincronizado.'}
              </p>
              {sync && <p className={clsx('text-small', sync.ok ? 'text-emerald-700' : 'text-red-600')}>{sync.message}</p>}
              {data.provider_usage.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="table-header">
                      <tr>
                        <th className="text-left">Fornecedor</th>
                        <th className="text-left">Modelo / item</th>
                        <th className="text-right">Requisições</th>
                        <th className="text-right">Tokens (entrada / saída)</th>
                        <th className="text-right">Custo informado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.provider_usage.map((p) => (
                        <tr key={`${p.vendor}-${p.model}`} className="table-row">
                          <td className="table-cell">{p.vendor}</td>
                          <td className="table-cell font-mono text-xs">{p.model}</td>
                          <td className="table-cell text-right">{formatIntBR(p.requests)}</td>
                          <td className="table-cell text-right">
                            {formatIntBR(p.input_tokens)} / {formatIntBR(p.output_tokens)}
                          </td>
                          <td className="table-cell text-right">{formatUSD(p.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-small text-slate-600 mt-2 flex items-center gap-1">
                    <Activity size={14} /> Total informado: {formatUSD(providerTotal, 2)} · registrado pelo sistema: {formatUSD(t.cost_usd, 2)}
                    {providerTotal > 0 && ` · diferença ${formatUSD(providerTotal - t.cost_usd, 2)}`}
                  </p>
                </div>
              )}
            </section>

            <CallTable title="Chamadas mais caras" rows={data.top_calls} />
            <CallTable title="Erros recentes" rows={data.recent_errors} icon={<AlertTriangle size={16} className="text-red-500" />} />
          </>
        )
      )}
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
